import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bell, BellOff, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { pushState, subscribePush, thisDeviceSubId, unsubscribeLocal } from "@/lib/pwa";

export const IOS_COPY = "On iPhone, add the app to your Home Screen first, then turn on notifications";
export const DENIED_COPY = "Notifications are blocked. Turn them on in your phone/browser settings.";

function Notice({ tone, children, testId }) {
  const cls = tone === "warn" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-rose-50 border-rose-200 text-rose-900";
  return <p className={`text-sm border rounded-xl px-3 py-2 ${cls}`} data-testid={testId}>{children}</p>;
}

export function PushCard() {
  const [subs, setSubs] = useState(null);
  const [state, setState] = useState(pushState());
  const [busy, setBusy] = useState(false);
  const mine = thisDeviceSubId();
  const load = useCallback(() => api.get("/push/subscriptions").then((r) => setSubs(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const here = subs?.some((s) => s.id === mine);

  const turnOn = async () => {
    setBusy(true);
    try {
      const r = await subscribePush(api);
      if (r.status === "denied") setState("denied");
      else if (r.status === "ok") { toast.success("Notifications turned on for this device"); load(); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const test = async (id) => {
    try { await api.post(`/push/subscriptions/${id}/test`); toast.success("Test notification sent"); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };
  const remove = async (id) => {
    await api.delete(`/push/subscriptions/${id}`);
    if (id === mine) await unsubscribeLocal().catch(() => {});
    toast.success("Device removed");
    load();
  };

  return (
    <section className="surface p-5 sm:p-6 mb-6 space-y-3 fade-up" data-testid="push-card">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Bell className="h-5 w-5" />Phone & computer notifications</h2>
      <p className="text-sm text-muted-foreground">Pop-up alerts for due bills, budget alerts and new scans waiting for review.</p>
      {state === "ios-not-installed" && <Notice tone="warn" testId="push-ios-not-installed">{IOS_COPY}. <Link to="/install" className="underline font-semibold">How to install</Link></Notice>}
      {state === "denied" && <Notice testId="push-denied">{DENIED_COPY}</Notice>}
      {state === "unsupported" && <Notice tone="warn" testId="push-unsupported">This browser can't show notifications. Try Chrome, Edge or the installed app.</Notice>}
      {state === "ok" && !here && (
        <Button onClick={turnOn} disabled={busy} className="h-11 rounded-xl" data-testid="push-turn-on-button"><Bell className="h-4 w-4 mr-2" />{busy ? "Turning on…" : "Turn on notifications"}</Button>
      )}
      <ul className="divide-y divide-border" data-testid="push-device-list">
        {subs?.length === 0 && <li className="py-2 text-sm text-muted-foreground flex items-center gap-2" data-testid="push-no-devices"><BellOff className="h-4 w-4" />No devices yet</li>}
        {subs?.map((s) => (
          <li key={s.id} className="py-3 flex flex-wrap items-center gap-2" data-testid={`push-device-${s.id}`}>
            <Smartphone className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium flex-1 min-w-[8rem]">{s.device}{s.id === mine && <span className="ml-2 text-xs text-emerald-700">(this device)</span>}</span>
            <Button size="sm" variant="outline" className="h-9 rounded-lg" onClick={() => test(s.id)} data-testid={`push-test-${s.id}`}>Send test notification</Button>
            <Button size="sm" variant="outline" className="h-9 rounded-lg text-rose-700" onClick={() => remove(s.id)} data-testid={`push-remove-${s.id}`}>Remove</Button>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground"><Link to="/install" className="underline" data-testid="push-install-link">Install the app</Link> for the best experience on your phone.</p>
    </section>
  );
}
